import { StreamLanguage, HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags } from "@lezer/highlight";

interface JinjaState {
  inComment: boolean;
  inTag: boolean;
  inVar: boolean;
}

/**
 * A minimal CodeMirror StreamLanguage for Jinja2 template syntax.
 *
 * Only the three Jinja constructs are tokenized:
 *   {# ... #}  → "comment"
 *   {% ... %}  → "keyword"
 *   {{ ... }}  → "variableName"
 *
 * Everything else returns null — no token — so surrounding prose inherits
 * the editor's default text color and is never accidentally recolored.
 */
export const jinjaLanguage = StreamLanguage.define<JinjaState>({
  name: "jinja2",

  startState: () => ({ inComment: false, inTag: false, inVar: false }),

  token(stream, state) {
    // Inside an open {# comment #}
    if (state.inComment) {
      if (stream.match(/#\}/)) {
        state.inComment = false;
        return "comment";
      }
      stream.next();
      return "comment";
    }

    // Inside an open {% tag %}
    if (state.inTag) {
      if (stream.match(/%\}/)) {
        state.inTag = false;
        return "keyword";
      }
      stream.next();
      return "keyword";
    }

    // Inside an open {{ variable }}
    if (state.inVar) {
      if (stream.match(/\}\}/)) {
        state.inVar = false;
        return "variableName";
      }
      stream.next();
      return "variableName";
    }

    // Opening delimiters — priority: comment > tag > variable
    if (stream.match(/\{#/)) {
      state.inComment = true;
      return "comment";
    }
    if (stream.match(/\{%/)) {
      state.inTag = true;
      return "keyword";
    }
    if (stream.match(/\{\{/)) {
      state.inVar = true;
      return "variableName";
    }

    // Plain text — consume one character, return no token.
    stream.next();
    return null;
  },
});

/** Default colors for each Jinja token type. */
export const JINJA_TOKEN_COLORS = {
  comment: "rgb(19,128,59)", // {# comment #}
  tag: "rgb(28,75,177)", // {% tag %}
  variable: "rgb(158,18,8)", // {{ variable }}
};

/**
 * CodeMirror syntaxHighlighting extension that maps Jinja token types to their
 * display colors. Import this alongside jinjaLanguage when setting up an editor.
 */
export const jinjaHighlighting = syntaxHighlighting(
  HighlightStyle.define([
    { tag: tags.comment, color: JINJA_TOKEN_COLORS.comment },
    { tag: tags.keyword, color: JINJA_TOKEN_COLORS.tag },
    { tag: tags.variableName, color: JINJA_TOKEN_COLORS.variable },
  ])
);
